import { getAccessToken } from "../lib/auth";
import { parseApiError } from "../lib/api-error";

const API_BASE = "/api/backup";

// =============================================================================
// Types
// =============================================================================

export interface CNPGClusterCondition {
  type: string;
  status: string;
  message?: string;
  reason?: string;
  lastTransitionTime?: string;
}

export interface CNPGCluster {
  name: string;
  namespace: string;
  instances: number;
  readyInstances: number;
  phase: string;
  primaryPod?: string;
  lastBackupTime?: string;
  firstRecoverabilityPoint?: string;
  databaseName?: string;
  hasBackupConfig: boolean;
  storageSize?: string;
  image?: string;
  conditions: CNPGClusterCondition[];
  walArchivingStatus?: "ok" | "failed" | "unknown";
  s3DestinationPath?: string;
  retentionPolicy?: string;
  postgresParameters?: Record<string, string>;
  dbOwner?: string;
  writeService?: string;
  readService?: string;
  healthyInstances?: string[];
  certificateExpirations?: Record<string, string>;
  currentPrimaryTimestamp?: string;
  timelineID?: number;
  scheduledBackup?: CNPGScheduledBackup | null;
}

export interface CNPGBackup {
  name: string;
  namespace: string;
  clusterName: string;
  phase: string;
  startedAt?: string;
  completedAt?: string;
  method?: string;
  error?: string;
  instancePod?: string;
  serverName?: string;
  destinationPath?: string;
}

export interface CNPGScheduledBackup {
  name: string;
  namespace: string;
  clusterName: string;
  schedule: string;
  backupOwnerReference: string;
  lastCheckTime?: string;
  lastScheduleTime?: string;
  nextScheduleTime?: string;
}

export interface ClusterDetailResponse {
  cluster: Omit<CNPGCluster, "hasBackupConfig" | "scheduledBackup"> & {
    hasBackupConfig?: boolean;
  };
  backups: CNPGBackup[];
  scheduledBackup: CNPGScheduledBackup | null;
}

export interface BackupOperation {
  id: string;
  operationType: "backup" | "restore" | "prod_to_dev";
  targetCluster: string;
  targetNamespace: string;
  sourceCluster?: string;
  sourceNamespace?: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  statusMessage?: string;
  k8sResourceName?: string;
  k8sResourceKind?: string;
  anonymize: boolean;
  recoveryTargetTime?: string;
  options?: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface OperationsResponse {
  items: BackupOperation[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

// =============================================================================
// Fetch helper
// =============================================================================

async function fetchBackupAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

// =============================================================================
// API
// =============================================================================

export const backupApi = {
  listClusters: () =>
    fetchBackupAPI<{ clusters: CNPGCluster[] }>("/clusters"),

  getClusterDetail: (ns: string, name: string) =>
    fetchBackupAPI<ClusterDetailResponse>(`/clusters/${ns}/${name}`),

  listBackups: (ns: string, name: string) =>
    fetchBackupAPI<{ backups: CNPGBackup[] }>(`/clusters/${ns}/${name}/backups`),

  triggerBackup: (ns: string, name: string) =>
    fetchBackupAPI<{ operationId: string }>(`/clusters/${ns}/${name}/backup`, {
      method: "POST",
    }),

  listOperations: (params?: { type?: string; status?: string; cluster?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.status) qs.set("status", params.status);
    if (params?.cluster) qs.set("cluster", params.cluster);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return fetchBackupAPI<OperationsResponse>(`/operations${query ? `?${query}` : ""}`);
  },

  getOperation: (id: string) =>
    fetchBackupAPI<BackupOperation>(`/operations/${id}`),

  triggerRestore: (data: { clusterName: string; namespace: string; recoveryTargetTime?: string }) =>
    fetchBackupAPI<{ operationId: string }>("/restore", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  triggerProdToDev: (data: {
    sourceCluster: string;
    sourceNamespace: string;
    targetCluster: string;
    targetNamespace: string;
    anonymize: boolean;
  }) =>
    fetchBackupAPI<{ operationId: string }>("/prod-to-dev", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  cancelOperation: (id: string) =>
    fetchBackupAPI<{ message: string }>(`/operations/${id}`, { method: "DELETE" }),
};
